import { yupResolver } from '@hookform/resolvers/yup';
import { ToastNotification } from 'components/common';
import { useContext, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import {
  cssTransition,
  toast,
  ToastOptions,
  ToastPromiseParams,
} from 'react-toastify';
import { FilterContext } from 'utils/context';
import { TranslateFn } from 'utils/types/translations';
import { object, string } from 'yup';

import { Button, Flex, Icon, styled, TextField } from '@project44/manifest-ds';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@project44/manifest-ui-deprecated';
import { useTranslation } from '@project44/translations-utils';

import { AUTO_DISMISS_DELAY } from './constants';
import { exportFileAsync } from './utils';

const TriggerButton = styled(Button, {
  boxShadow: 'none',
  border: '1px solid $palette-grey-200',
  variants: {
    isActive: {
      true: {
        borderColor: '$palette-indigo-600',
        color: '$palette-indigo-600',
        backgroundColor: '$palette-indigo-50',
        '&:hover': {
          backgroundColor: '$palette-indigo-50',
        },
      },
    },
  },
});

const Form = styled('form', {
  backgroundColor: '$palette-white',
  padding: '$medium',
  borderRadius: '$small',
  boxShadow: '$medium',
  maxWidth: '600px',
});

const FormInside = styled('div', {
  display: 'flex',
  gap: '$small',
  justifyContent: 'space-between',
});

const FormDescription = styled('p', {
  color: '$palette-grey-600',
  fontWeight: '$regular',
  fontSize: '$small',
  margin: 0,
});

const FormTitle = styled('h3', {
  fontSize: '$medium',
  letterSpacing: '$x-small',
  margin: 0,
});

const PendingToastNotification = styled('p', {
  color: '$palette-grey-900',
  fontWeight: '$semibold',
  textAlign: 'left',
  margin: 0,
  padding: '$small 0',
});

interface FormValues {
  email: string;
}

const toastOptions: ToastOptions = {
  position: 'top-right',
  autoClose: AUTO_DISMISS_DELAY,
  hideProgressBar: true,
};

const promiseTransition = cssTransition({
  enter: 'grow-in',
  exit: 'slide-out-right',
});

interface IGetToastPromiseParams {
  translateFn: TranslateFn;
}

const getToastPromiseParams = ({
  translateFn,
}: IGetToastPromiseParams): ToastPromiseParams => {
  return {
    pending: {
      transition: cssTransition({
        enter: 'slide-in-right',
        exit: 'fade-out',
      }),
      render: () => {
        return (
          <PendingToastNotification>
            {translateFn('core.exportProcessing')}
          </PendingToastNotification>
        );
      },
    },
    success: {
      transition: promiseTransition,
      icon: () => (
        <Icon icon="check_circle" css={{ color: '$palette-green-600' }} />
      ),
      render: () => {
        return (
          <ToastNotification
            title={translateFn('core.exportSuccessTitle')}
            description={translateFn('core.exportSuccessDescription')}
          />
        );
      },
    },
    error: {
      transition: promiseTransition,
      icon: () => (
        <Icon
          icon="do_not_disturb_on"
          css={{ color: '$palette-red-600', transform: 'rotate(45deg)' }}
        />
      ),
      render: () => {
        return (
          <ToastNotification
            title={translateFn('core.exportErrorTitle')}
            description={translateFn('core.exportErrorDescription')}
          />
        );
      },
    },
  };
};

const validationSchema = object().shape({
  email: string().email('core.invalidEmail').required('core.requiredField'),
});

const formResolver = yupResolver(validationSchema);

interface IExportCsvProps {
  showLimitWarning?: boolean;
}

function ExportCsv({ showLimitWarning = false }: IExportCsvProps) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getShipmentsRequest } = useContext(FilterContext);
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: {
      errors,
      isValid,
      isSubmitted,
      isSubmitSuccessful,
      isSubmitting,
    },
  } = useForm<FormValues>({
    resolver: (...args) => formResolver<FormValues, unknown>(...args),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ email: '' });
      setIsModalOpen(false);
    }
  }, [isSubmitSuccessful, reset]);

  const toastPromiseParams = getToastPromiseParams({ translateFn: t });

  const onSubmit = ({ email }: FieldValues) => {
    const searchCriteria = getShipmentsRequest();
    toast.promise(
      exportFileAsync({ email, searchCriteria }),
      toastPromiseParams,
      toastOptions,
    );
  };

  const { onChange: _onChange, ...emailRegister } = register('email');

  const isFormInvalid = isSubmitted && !isValid;

  return (
    <Popover isOpen={isModalOpen} onChange={setIsModalOpen}>
      <PopoverTrigger>
        <TriggerButton
          data-testid="export-btn"
          isActive={isModalOpen}
          variant="secondary"
          size="small"
          startIcon={
            <Icon
              icon="file_download"
              css={{
                fontSize: '$medium',
                color: isModalOpen
                  ? '$palette-indigo-600'
                  : '$palette-grey-700',
              }}
            />
          }
        >
          {t('global.export')}
        </TriggerButton>
      </PopoverTrigger>
      <PopoverContent>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormTitle css={{ marginBottom: '$small' }}>
            {showLimitWarning ? (
              <Flex css={{ gap: '$small' }}>
                <Icon icon="info" css={{ color: '$palette-orange-600' }} />
                {t('core.exportLimitWarning')}
              </Flex>
            ) : (
              t('core.csvExport')
            )}
          </FormTitle>
          <FormDescription css={{ marginBottom: '$medium' }}>
            {showLimitWarning
              ? t('core.exportLimitWarningDescription')
              : t('core.exportLimitDescription')}
          </FormDescription>
          <FormInside>
            <TextField
              aria-label={t('core.emailAddress')}
              placeholder={t('core.emailAddress')}
              startIcon={<Icon icon="email" />}
              helperText={errors.email?.message && t(errors.email?.message)}
              validationState={isFormInvalid ? 'invalid' : 'valid'}
              onChange={(nextValue) => setValue('email', nextValue)}
              helperTextProps={{
                role: 'alert',
              }}
              {...emailRegister}
            />
            <Button type="submit" isDisabled={isFormInvalid || isSubmitting}>
              {t('core.sendDownloadLink')}
            </Button>
          </FormInside>
        </Form>
      </PopoverContent>
    </Popover>
  );
}

export default ExportCsv;
